import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import swal from 'sweetalert';

import TextEditor from '../../../elements/formElements/TextEditor';
import axios from '../../../services/MyAxios';
import { CASINO_FREESPIN_CAMPAIGNS } from '../Helpers/CrmUrls';

function FreespinCampaignCreate() {
    const [campaign, setCampaign] = useState({
        name: '',
        casino_freespin_page_id: 0,
        quantity_per_user: 1,
        currency_code: process.env.MIX_CURRENCY,
        image_base64: '',
        creator_name: '',
        casino_game_id: 0,
        min_deposit: 0,
        bet_option_id: 0,
        denomination: 0,
        wagering_requirement: 0,
        count_of_days_to_fill_requirements_by_user: 1,
        activation_date_from: '',
        activation_date_to: '',
        additional_data: null,
        description: '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    const [gameCreators, setGameCreators] = useState([]);
    const [games, setGames] = useState([]);
    const [freeSpinOptions, setFreeSpinOptions] = useState(null);
    const [freespinPages, setFreespinPages] = useState([]);

    const [errors, setErrors] = useState([]);
    const history = useHistory();

    function handleFieldChange(event) {
        setCampaign({ ...campaign, [event.target.name]: event.target.value });
    }

    function handleProviderChange(event) {
        handleFieldChange(event);
        setFreeSpinOptions(null);
        axios.get(`/crea/casino/api/freespin-campaigns/game-creators/${event.target.value}/games`).then((response) => {
            setGames(response.data);
        });
    }

    const handleEditorChange = (description) => {
        setCampaign({ ...campaign, description });
    };

    useEffect(() => {
        axios.get('/crea/casino/api/freespin-campaigns/game-creators').then((response) => {
            setGameCreators(response.data);
        });

        axios.get('/crea/casino/api/freespin-campaigns/get-pages-in-primary-language').then((response) => {
            setFreespinPages(response.data);
        });
    }, []);

    useEffect(() => {
        if (campaign.casino_game_id !== 0 && campaign.creator_name + campaign.currency_code !== '') {
            axios
                .get(
                    `/crea/casino/api/freespin-campaigns/freespin-options?creator_name=${campaign.creator_name}&currency_code=${campaign.currency_code}&casino_game_id=${campaign.casino_game_id}`
                )
                .then((response) => {
                    setFreeSpinOptions(response.data);
                })
                .catch((e) => {
                    setFreeSpinOptions(null);
                    swal({
                        title: 'Error',
                        text: e.response.data || 'Something went wrong...',
                        icon: 'error',
                    });
                });
        }
    }, [campaign.creator_name && campaign.currency_code && campaign.casino_game_id]);

    function handleCreateNewCampaign(event) {
        event.preventDefault();

        if (freeSpinOptions === null) {
            swal({
                title: 'Error',
                text: 'Game does not provide required freespin options',
                icon: 'error',
            });
            return;
        }

        campaign.additional_data = JSON.stringify(freeSpinOptions);

        axios
            .post('/crea/casino/api/freespin-campaigns/create', campaign)
            .then((response) => {
                if (response.data) {
                    swal({
                        title: 'Success',
                        text: 'Freespin campaign created correctly',
                        icon: 'success',
                    }).then(() => {
                        history.push(CASINO_FREESPIN_CAMPAIGNS);
                    });
                    return;
                }

                swal({
                    title: 'Error',
                    text: response.data.message || 'Something went wrong...',
                    icon: 'error',
                });
            })
            .catch((error) => {
                if (!error.response) {
                    setErrors(error.message);
                } else if (error.response.status !== 500) {
                    setErrors(error.response.data.errors);
                } else {
                    swal({
                        title: 'Error',
                        text: 'Server error',
                        icon: 'error',
                    });
                }
            });
    }

    function hasErrorFor(field) {
        return !!errors[field];
    }

    function renderErrorFor(field) {
        if (hasErrorFor(field)) {
            return (
                <span className="invalid-feedback" style={{ display: 'block' }}>
                    <strong>{errors[field][0]}</strong>
                </span>
            );
        }
        return '';
    }

    function convertBase64(file) {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);

            fileReader.onload = () => {
                resolve(fileReader.result);
            };

            fileReader.onerror = (error) => {
                reject(error);
            };
        });
    }

    async function handleImageChange(event) {
        const file = event.target.files[0];
        const base64 = await convertBase64(file);

        setCampaign({
            ...campaign,
            image_base64: base64,
        });
    }

    return (
        <div className="d-flex justify-content-between">
            <div className="card w-50 mr-3 h-100">
                <form onSubmit={handleCreateNewCampaign}>
                    <div className="card-body">
                        <div className="form-group">
                            <label className="d-block" htmlFor="name">
                                Name <span className="text-danger text-sm">*</span>
                                <input
                                    id="name"
                                    type="text"
                                    className={`form-control ${hasErrorFor('name') ? 'is-invalid' : ''}`}
                                    name="name"
                                    value={campaign.name}
                                    onChange={handleFieldChange}
                                />
                            </label>
                            {renderErrorFor('name')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="casino_freespin_page_id">
                                Freespin page <span className="text-danger text-sm">*</span>
                                <select
                                    name="casino_freespin_page_id"
                                    id="casino_freespin_page_id"
                                    onChange={handleFieldChange}
                                    className={`form-control ${
                                        hasErrorFor('casino_freespin_page_id') ? 'is-invalid' : ''
                                    }`}
                                >
                                    <option defaultValue="">-- Freespin page --</option>
                                    {freespinPages.map((freespinPage) => (
                                        <option key={freespinPage.id} value={freespinPage.id}>
                                            {freespinPage.slug}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {renderErrorFor('casino_freespin_page_id')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="quantity_per_user">
                                Quantity per user <span className="text-danger text-sm">*</span>
                                <input
                                    id="quantity_per_user"
                                    type="number"
                                    min="1"
                                    className={`form-control ${hasErrorFor('quantity_per_user') ? 'is-invalid' : ''}`}
                                    name="quantity_per_user"
                                    value={campaign.quantity_per_user}
                                    onChange={handleFieldChange}
                                />
                            </label>
                            {renderErrorFor('quantity_per_user')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="creator_name">
                                Game creator <span className="text-danger text-sm">*</span>
                                <select
                                    name="creator_name"
                                    id="creator_name"
                                    onChange={handleProviderChange}
                                    className={`form-control ${hasErrorFor('creator_name') ? 'is-invalid' : ''}`}
                                >
                                    <option defaultValue="">-- Game creator --</option>
                                    {gameCreators.map((creator) => (
                                        <option
                                            key={(creator.provider_id, creator.creator_name)}
                                            value={creator.creator_name}
                                        >
                                            {creator.creator_name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {renderErrorFor('creator_name')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="casino_game_id">
                                Game <span className="text-danger text-sm">*</span>
                                <select
                                    name="casino_game_id"
                                    id="casino_game_id"
                                    onChange={handleFieldChange}
                                    className={`form-control ${hasErrorFor('casino_game_id') ? 'is-invalid' : ''}`}
                                >
                                    <option defaultValue="">-- Game --</option>
                                    {games.map((game) => (
                                        <option key={game.id} value={game.id}>
                                            {game.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {renderErrorFor('casino_game_id')}
                        </div>
                        <div className="form-group">
                            <label htmlFor="image_base64">
                                Image 360 x 268 px in .png format:
                                <br/>
                                <input
                                    className="form-control"
                                    type="file"
                                    accept=".png"
                                    name="image_base64"
                                    onChange={handleImageChange}
                                />
                            </label>
                            {renderErrorFor('image_base64')}
                        </div>
                        <div className="form-group">
                            {freeSpinOptions?.bets && (
                                <label className="d-block" htmlFor="bet_option_id">
                                    Bet id <span className="text-danger text-sm">*</span>
                                    <select
                                        name="bet_option_id"
                                        id="bet_option_id"
                                        onChange={handleFieldChange}
                                        className={`form-control ${hasErrorFor('bet_option_id') ? 'is-invalid' : ''}`}
                                    >
                                        <option defaultValue="">-- Bet id --</option>
                                        {freeSpinOptions?.bets.map((bet) => (
                                            <option key={bet.bet_id} value={bet.bet_id}>
                                                {bet.bet_id}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {renderErrorFor('bet_option_id')}
                        </div>
                        <div className="form-group">
                            {freeSpinOptions?.denominations && (
                                <label className="d-block" htmlFor="denomination">
                                    Denomination <span className="text-danger text-sm">*</span>
                                    <select
                                        name="denomination"
                                        id="denomination"
                                        onChange={handleFieldChange}
                                        className={`form-control ${hasErrorFor('denomination') ? 'is-invalid' : ''}`}
                                    >
                                        <option defaultValue="">-- Denomination --</option>
                                        {freeSpinOptions?.denominations.map((denomination, index) => (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <option key={index} value={denomination}>
                                                {denomination}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {renderErrorFor('denomination')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="count_of_days_to_fill_requirements_by_user">
                                Count of days to fill requirements by user
                                <span className="text-danger text-sm">*</span>
                                <input
                                    id="count_of_days_to_fill_requirements_by_user"
                                    type="number"
                                    min="1"
                                    className={`form-control ${
                                        hasErrorFor('count_of_days_to_fill_requirements_by_user') ? 'is-invalid' : ''
                                    }`}
                                    name="count_of_days_to_fill_requirements_by_user"
                                    value={campaign.count_of_days_to_fill_requirements_by_user}
                                    onChange={handleFieldChange}
                                />
                            </label>
                            {renderErrorFor('count_of_days_to_fill_requirements_by_user')}
                        </div>
                        <div className="form-group">You are using {campaign.timezone} timezone</div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="activation_date_from">
                                Available to activate from:
                                <span className="text-danger text-sm">*</span>
                                <input
                                    id="activation_date_from"
                                    type="datetime-local"
                                    className={`form-control ${
                                        hasErrorFor('activation_date_from') ? 'is-invalid' : ''
                                    }`}
                                    min={new Date().toISOString().slice(0, new Date().toISOString().lastIndexOf(':'))}
                                    name="activation_date_from"
                                    value={campaign.activation_date_from}
                                    onChange={handleFieldChange}
                                />
                            </label>
                            {renderErrorFor('activation_date_from')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="activation_date_to">
                                Available to activate to:
                                <span className="text-danger text-sm">*</span>
                                <input
                                    id="activation_date_to"
                                    type="datetime-local"
                                    className={`form-control ${hasErrorFor('activation_date_to') ? 'is-invalid' : ''}`}
                                    min={new Date().toISOString().slice(0, new Date().toISOString().lastIndexOf(':'))}
                                    name="activation_date_to"
                                    value={campaign.activation_date_to}
                                    onChange={handleFieldChange}
                                />
                            </label>
                            {renderErrorFor('activation_date_to')}
                        </div>
                        <div className="form-group">
                            <label htmlFor="description">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={campaign.description}
                                onChange={handleFieldChange}
                                className={`form-control
                                        ${hasErrorFor('description') ? 'is-invalid' : ''}`}
                            />
                            {renderErrorFor('description')}
                        </div>
                    </div>
                    <hr/>
                    <div className="card-body">
                        <h5 className="card-title">Set rules</h5>
                        <div className="form-group">
                            <label className="d-block" htmlFor="min_deposit">
                                Min deposit <span className="text-danger text-sm">*</span>
                                <div className="d-flex">
                                    <input
                                        id="min_deposit"
                                        type="number"
                                        min={0}
                                        className={`form-control w-75 ${
                                            hasErrorFor('min_deposit') ? 'is-invalid' : ''
                                        }`}
                                        name="min_deposit"
                                        value={campaign.min_deposit}
                                        onChange={handleFieldChange}
                                    />
                                    <input
                                        className="w-25 text-center"
                                        type="text"
                                        disabled
                                        value={campaign.currency_code}
                                    />
                                </div>
                            </label>
                            {renderErrorFor('min_deposit')}
                        </div>
                        <div className="form-group">
                            <label className="d-block" htmlFor="wagering_requirement">
                                Wagering requirement <span className="text-danger text-sm">*</span>
                                <div className="d-flex">
                                    <input
                                        id="wagering_requirement"
                                        type="number"
                                        min={0}
                                        className={`form-control w-75 ${
                                            hasErrorFor('wagering_requirement') ? 'is-invalid' : ''
                                        }`}
                                        name="wagering_requirement"
                                        value={campaign.wagering_requirement}
                                        onChange={handleFieldChange}
                                    />
                                    <input className="w-25 text-center" type="text" disabled value="X" />
                                </div>
                            </label>
                            {renderErrorFor('wagering_requirement')}
                        </div>
                        <button type="submit" className="btn btn-primary text-center">
                            Create new campaign
                        </button>
                    </div>
                </form>
            </div>
            <div className="card w-50 overflow-auto">
                <div className="card-body">
                    <h5 className="card-title">Freespin options</h5>
                    <pre>
                        {freeSpinOptions !== null ? JSON.stringify(freeSpinOptions, null, 2) : 'No freespin options'}
                    </pre>
                </div>
            </div>
        </div>
    );
}

export default FreespinCampaignCreate;
